import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import favicon from "./assets/images/favicon.png";
import { useAuthStore } from "./store/authStore";
import { ROUTES, RECAPTCHA_SITE_KEY } from "./constants";
import { PublicRoute } from "./components/routes/PublicRoute";
import { ProtectedRoute } from "./components/routes/ProtectedRoute";
import { CompanyOnlyRoute } from "./components/routes/CompanyOnlyRoute";
import {
  PermissionRoute,
  CompanyProfileRoute,
} from "./components/routes/PermissionRoute";
import { PageId, ActionCode } from "./types/index";
import { AppProviders } from "./providers/AppProviders";
import { UpgradePaymentRedirect } from "./components/routes/UpgradePaymentRedirect";
import {
  LoginPage,
  ForgotPasswordPage,
  CompanyFormPage,
  DashboardPage,
  EmployeesPage,
  EmployeeFormPage,
  EmployeeScansPage,
  PublicEmployeePage,
  PublicIndividualPage,
  CompaniesPage,
  CompanyScansPage,
  IndividualsPage,
  IndividualScansPage,
  PackageFormPage,
  PackagesPage,
  UsersPage,
  UserFormPage,
  LicensesPage,
  InvoicesPage,
  LookupsPage,
  LookupFormPage,
  ContactUsPage,
  ContactUsFormPage,
  HomePage,
  ContactPage,
  AboutPage,
  FeaturesPage,
  PricingPage,
  FaqPage,
  PrivacyPage,
  DataPolicyPage,
  TermsPage,
  DemoPage,
  PaymentResultPage,
  UpgradePackagePage,
  NotFoundPage,
} from "./routes/lazyPages";
import { getBasePathWithoutTrailingSlash } from "./utils/basePath";
import { RouteSeo } from "./components/seo";

function App() {
  const initializeAuth = useAuthStore((state) => state.initializeAuth);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  useEffect(() => {
    const reinitialize = () => initializeAuth();
    const onStorage = (e: StorageEvent) => {
      if (!e.key) return;
      const k = e.key.toLowerCase();
      if (
        k === "__auth_event__" ||
        k === "access_token" ||
        k === "refresh_token" ||
        k === "user_data"
      ) {
        initializeAuth();
      }
    };

    window.addEventListener(
      "auth:token-changed",
      reinitialize as EventListener
    );
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(
        "auth:token-changed",
        reinitialize as EventListener
      );
      window.removeEventListener("storage", onStorage);
    };
  }, [initializeAuth]);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/svg";
    link.href = favicon;
    document.head.appendChild(link);
  }, []);

  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <BrowserRouter basename={getBasePathWithoutTrailingSlash()}>
        <RouteChangeTracker />
        <RouteSeo />
        <AppProviders>
          <Routes>
            <Route
              path={ROUTES.LOGIN}
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.FORGOTPASSWORD_WITH_ID}
              element={
                <PublicRoute>
                  <ForgotPasswordPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.FORGOTPASSWORD}
              element={
                <PublicRoute>
                  <ForgotPasswordPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.REGISTER}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <CompanyFormPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.REGISTER_Package}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <CompanyFormPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.USER_VIEW}
              element={
                <ProtectedRoute>
                  <UserFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.USER_NEW}
              element={
                <ProtectedRoute>
                  <UserFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.USER_EDIT}
              element={
                <ProtectedRoute>
                  <UserFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.EMPLOYEE_VIEW}
              element={
                <CompanyOnlyRoute>
                  <EmployeeFormPage />
                </CompanyOnlyRoute>
              }
            />
            <Route
              path={ROUTES.EMPLOYEE_SCANS}
              element={
                <CompanyOnlyRoute>
                  <PermissionRoute
                    pageId={PageId.EMPLOYEES}
                    actionCode={ActionCode.VIEW}
                  >
                    <EmployeeScansPage />
                  </PermissionRoute>
                </CompanyOnlyRoute>
              }
            />
            <Route
              path={ROUTES.EMPLOYEE_PUBLIC}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <PublicEmployeePage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.INDIVIDUAL_PUBLIC}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <PublicIndividualPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.EMPLOYEE_NEW}
              element={
                <CompanyOnlyRoute>
                  <EmployeeFormPage />
                </CompanyOnlyRoute>
              }
            />
            <Route
              path={ROUTES.EMPLOYEE_EDIT}
              element={
                <CompanyOnlyRoute>
                  <EmployeeFormPage />
                </CompanyOnlyRoute>
              }
            />

            <Route
              path={ROUTES.COMPANY_VIEW}
              element={
                <ProtectedRoute>
                  <CompanyFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.COMPANY_NEW}
              element={
                <ProtectedRoute>
                  <CompanyFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.COMPANY_EDIT}
              element={
                <ProtectedRoute>
                  <CompanyFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.COMPANY_PROFILE}
              element={
                <ProtectedRoute>
                  <CompanyProfileRoute>
                    <CompanyFormPage />
                  </CompanyProfileRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.MY_PROFILE}
              element={
                <ProtectedRoute>
                  <PermissionRoute
                    pageId={PageId.MY_PROFILE}
                    actionCode={ActionCode.VIEW}
                  >
                    <CompanyFormPage />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.PACKAGE_VIEW}
              element={
                <ProtectedRoute>
                  <PackageFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.PACKAGE_NEW}
              element={
                <ProtectedRoute>
                  <PackageFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.PACKAGE_EDIT}
              element={
                <ProtectedRoute>
                  <PackageFormPage />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.DASHBOARD}
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.UPGRADE_PACKAGE}
              element={
                <ProtectedRoute>
                  <UpgradePackagePage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.EMPLOYEES}
              element={
                <CompanyOnlyRoute>
                  <EmployeesPage />
                </CompanyOnlyRoute>
              }
            />

            <Route
              path={ROUTES.COMPANIES}
              element={
                <ProtectedRoute>
                  <CompaniesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.COMPANY_SCANS}
              element={
                <ProtectedRoute>
                  <PermissionRoute
                    pageId={PageId.COMPANIES}
                    actionCode={ActionCode.VIEW}
                  >
                    <CompanyScansPage />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.INDIVIDUALS}
              element={
                <ProtectedRoute>
                  <IndividualsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.INDIVIDUAL_SCANS}
              element={
                <ProtectedRoute>
                  <PermissionRoute
                    pageId={PageId.INDIVIDUALS}
                    actionCode={ActionCode.VIEW}
                  >
                    <IndividualScansPage />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.PACKAGES}
              element={
                <ProtectedRoute>
                  <PackagesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.USERS}
              element={
                <ProtectedRoute>
                  <UsersPage />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.LICENSES}
              element={
                <ProtectedRoute>
                  <LicensesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.INVOICES}
              element={
                <ProtectedRoute>
                  <PermissionRoute
                    pageId={PageId.INVOICES}
                    actionCode={ActionCode.VIEW}
                  >
                    <InvoicesPage />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.LOOKUPS}
              element={
                <ProtectedRoute>
                  <LookupsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.LOOKUP_NEW}
              element={
                <ProtectedRoute>
                  <LookupFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.LOOKUP_EDIT}
              element={
                <ProtectedRoute>
                  <LookupFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.HOME}
              element={
              <PublicRoute redirectIfAuthenticated={false}>
                  <HomePage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.CONTACTUS}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <ContactUsPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.CONTACTUS_VIEW}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <ContactUsFormPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.CONTACT}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <ContactPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.ABOUT}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <AboutPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.DEMO}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <DemoPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.FEATURES}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <FeaturesPage />
                </PublicRoute>
              }
            /> 
            <Route
              path={ROUTES.PRICING}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <PricingPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.FAQ}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <FaqPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.PRIVACY}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <PrivacyPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.DATA_POLICY}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <DataPolicyPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.TERMS}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <TermsPage />
                </PublicRoute>
              }
            />
            <Route
              path="/paymentResult"
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <PaymentResultPage />
                </PublicRoute>
              }
            /> 
            <Route
              path={ROUTES.UPGRADE_PAYMENT_WITH_ID}
              element={
                <ProtectedRoute>
                  <UpgradePaymentRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.UPGRADE_PAYMENT}
              element={
                <ProtectedRoute>
                  <UpgradePaymentRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.NOT_FOUND}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <NotFoundPage />
                </PublicRoute>
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AppProviders>
    </BrowserRouter>
    </GoogleReCaptchaProvider>
  );
}

export default App;

function RouteChangeTracker() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location]);
  return null;
}
